import { useMutation, useQuery } from '@apollo/client';
import { countRules, fromObject, RuleGroupInternalType, toObject } from '@procureai/query-builder';
import { digest } from 'jsum';
import { clone } from 'ramda';
import { useState, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

import { Domain, QueryParam } from '~/common/types';

import { LOAD_QUERY_QUERY } from './LoadQuery.gql';
import { SAVE_QUERY_MUTATION } from './SaveQuery.gql';

interface QueryState {
  query?: RuleGroupInternalType;
  id: string | null;
  checksum: string | null;
  rulesTotal: number;
  modified?: boolean;
}

type UsePersistedQueryResult = [
  RuleGroupInternalType | undefined,
  (rules: RuleGroupInternalType | undefined) => void,
  { rulesTotal: number; loading: boolean; error: any },
];

export const usePersistedQuery = (domain?: Domain, q?: string): UsePersistedQueryResult => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [queryState, setQueryState] = useState<QueryState>({ id: null, checksum: null, rulesTotal: 0 });

  const {
    data: queryData,
    loading: queryLoading,
    error: queryError,
  } = useQuery(LOAD_QUERY_QUERY, {
    variables: { id: q },
    skip: !q,
  });

  const [save, { data: mutationData, loading: mutationLoading, error: mutationError, called }] =
    useMutation(SAVE_QUERY_MUTATION);

  useEffect(() => {
    let timeout: any | null = null;

    const currentQuery = queryState.query;

    if (queryState.modified) {
      // debounce saving
      timeout = setTimeout(() => {
        const { count, group } = toObject(currentQuery);

        const checksum = digest(clone(group), 'SHA256', 'hex');

        if (checksum !== queryState.checksum) {
          if (count) {
            setQueryState((prev) => ({
              ...prev,
              rulesTotal: count,
              checksum,
              id: null,
            }));
            save({
              variables: {
                query: group,
                checksum,
              },
            });
          } else {
            setQueryState((prev) => ({
              ...prev,
              rulesTotal: 0,
              checksum: null,
              id: null,
              modified: false,
            }));
            const queryParams = new URLSearchParams(searchParams);
            queryParams.delete(QueryParam.QUERY);
            queryParams.delete(QueryParam.ENCODED_QUERY);
            setSearchParams(queryParams, { replace: true });
          }
        }
      }, 1000);
    }
    return () => {
      if (timeout !== null) {
        clearTimeout(timeout);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryState.query]);

  // change url after saving changed query
  useEffect(() => {
    if (called && !mutationLoading && !mutationError && mutationData && mutationData.insert_queries_one) {
      const id = mutationData.insert_queries_one.id;
      const queryParams = new URLSearchParams(searchParams);
      queryParams.set(QueryParam.QUERY, id);
      queryParams.delete(QueryParam.ENCODED_QUERY);
      setQueryState((prev) => ({ ...prev, id }));
      setSearchParams(queryParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mutationData]);

  // save loaded query in state
  useEffect(() => {
    const data = queryData?.queries_by_pk;
    if (data && data.id !== queryState.id) {
      const transformedQuery = fromObject(data.query);
      setQueryState({
        id: data.id,
        checksum: data.checksum,
        query: transformedQuery,
        rulesTotal: countRules(transformedQuery),
        modified: false,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryData]);

  // reset query when changing domains
  useEffect(() => {
    if (!q) {
      setQueryState((prev) => {
        // only reset if query-state was not reset yet
        if (prev.id !== null) {
          return { id: null, checksum: null, rulesTotal: 0 };
        }
        return prev;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domain, q]);

  const updateQuery = useCallback(
    (grp: RuleGroupInternalType | undefined) => {
      if (grp && grp.rules.length) {
        setQueryState((prev) => ({
          ...prev,
          query: grp,
          modified: true,
        }));
      } else {
        const queryParams = new URLSearchParams(searchParams);
        queryParams.delete(QueryParam.QUERY);
        queryParams.delete(QueryParam.ENCODED_QUERY);
        setQueryState({ id: null, checksum: null, rulesTotal: 0 });
        setSearchParams(queryParams, { replace: true });
      }
    },
    [searchParams, setSearchParams],
  );

  return [
    queryState.query,
    updateQuery,
    {
      rulesTotal: queryState.rulesTotal,
      loading: mutationLoading || queryLoading,
      error: mutationError || queryError,
    },
  ];
};
